import { DeleteOutlined, PlusOutlined, ScheduleOutlined } from '@ant-design/icons'
import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Select, Switch, Table, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import adGroupApi from 'src/component/app/adGroup/AdGroupApi'
import * as routes from 'src/constants/routes'
import dictionariesApi from 'src/util/DictionariesApi'
import { useDebounce } from 'src/util/useDebounce'
import { toSort } from 'src/util/useSorter'
import { AdGroupBean, CampaignBean, CreativeBean, FilterListBean, LanguageBean, RetargetingListBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { capitalizedView, getIabCategoriesAsTree } from '../../../util/Util'
import AnchorWrapper from '../anchor/AnchorWrapper'
import creativeApi from '../creative/CreativeApi'
import filterListApi from '../filterList/FilterListApi'
import geoApi from '../geo/GeoApi'
import GeoTargeting, { emptyGeoItem, GeoItem, toBean } from '../geo/GeoTargeting'
import { fromBean, MapPoint, MapSelector } from '../map/MapSelector'
import { WithLoadedMap } from '../map/WithLoadedMap'
import { ALWAYS_WORK, clear, renderDay, Scheduler } from '../schedule/Scheduler'
import './style.css'
import FcPeriodEnum = AdGroupBean.FcPeriodEnum

const FormItem = Form.Item
const Option = Select.Option
const Column = Table.Column

const precisionEnum = AdGroupBean.GeoPrecisionEnum
const biddingStrategyEnum = AdGroupBean.BiddingStrategyEnum
const pmpModeEnum = AdGroupBean.PmpModeEnum

export const getGeoPrecision = (geoPrecision: AdGroupBean.GeoPrecisionEnum) => {
    switch (geoPrecision) {
        case precisionEnum.GPSLocation: return 'GPS/Location'
        case precisionEnum.IPAddress: return 'IP address'
        case precisionEnum.GPSLocationAndIP: return 'GPS/Location and IP'
    }
}

export const getPriceLabel = (strategy: AdGroupBean.BiddingStrategyEnum) => {
    switch (strategy) {
        case biddingStrategyEnum.CPC: return 'Target CPC'
        case biddingStrategyEnum.CPMOptimized: return 'Max CPM'
        default: return 'Price'
    }
}

export const anchorItems = [
    {
        key: 'general',
        href: '#general',
        title: 'General',
    },
    {
        key: 'budget',
        href: '#budget',
        title: 'Budget',
    },
    {
        key: 'creatives',
        href: '#creatives',
        title: 'Creatives',
    },
    {
        key: 'targeting',
        href: '#targeting',
        title: 'Targeting',
    },
    {
        key: 'day-parting',
        href: '#day-parting',
        title: 'Day Parting',
    },
    {
        key: 'geo',
        href: '#geo',
        title: 'Geo',
    }
]

const formInfoItems: FormDataType[] = [
    FormDataType.Languages,
    FormDataType.Campaigns,
    FormDataType.IabCategories,
    FormDataType.RetargetingLists
]

const AdGroupCreate = () => {

    const [info, setInfo] = useState({
        languages: [],
        campaigns: [],
        iabCategories: [],
        retargetingLists: []
    })

    const [advertiserId, setAdvertiserId] = useState<number>()
    const [availableCreatives, setAvailableCreatives] = useState([])
    const [availableFilterLists, setAvailableFilterLists] = useState([])

    const navigateTo = useNavigate()
    const [error, setError] = useState(undefined)
    const [form] = Form.useForm()
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    const loaded = useRef(false)

    const [geoItems, setGeoItems] = useState<GeoItem[]>([emptyGeoItem()])

    const [schedule, setSchedule] = useState<number[][]>(ALWAYS_WORK)
    const [scheduleVisible, setScheduleVisible] = useState(false)

    const [kwList, setKwList] = useState<string[]>([])
    const [kwSearch, setKwSearch] = useState<string>('')
    const debouncedKwSearch = useDebounce(kwSearch, 500)

    const [mapPoint, setMapPoint] = useState<MapPoint>({})
    const [mapVisible, setMapVisible] = useState(false)

    const [params] = useSearchParams()
    const copyAdGroupId = params.get('copyAdGroupId')

    const loadData = async () => {
        try {
            const resp = await formDataLoader(...formInfoItems)
            setInfo(resp as any)
        } catch (e) {
            setError(e.error)
        }
    }

    const loadAvailableCreatives = async () => {
        const creativeData: any = await creativeApi.allByAdvertiser(advertiserId, toSort({ sort: 'id', direction: 'asc' }))
        const list = creativeData.data.map(item => item.data as CreativeBean)
        setAvailableCreatives(list)
    }

    const loadAvailableFilterLists = async () => {
        const filterListData: any = await filterListApi.allByAdvertiser(advertiserId, toSort({ sort: 'id', direction: 'asc' }))
        const list = filterListData.data.map(item => item.data as FilterListBean)
        setAvailableFilterLists(list)
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleSubmit = async (path: string | undefined) => {
        const values = await form.validateFields()
        try {
            const response = await adGroupApi.create({ ...values, startDate, endDate, geoItems: geoItems.map(toBean), ...mapPoint, schedule: clear(schedule) })
            const id = response.data?.id

            navigateTo(path ? `${path}?advertiserId=${advertiserId}&adGroupId=${id}` : '/ad-group')
        } catch (e) {
            setError(e.error)
        }
    }

    const campaignId = Form.useWatch('campaignId', form)
    const creativeIds = Form.useWatch('creativeIds', form)
    const biddingStrategy = Form.useWatch('biddingStrategy', form)
    const antiFraudFcEnabled = Form.useWatch('antiFraudFcEnabled', form)
    const pmpMode = Form.useWatch('pmpMode', form)

    useEffect(() => {
        if (campaignId) {
            const campaign = (info.campaigns as CampaignBean[]).find(c => c.id == campaignId)
            const advId = campaign?.advertiserId
            setAdvertiserId(advId)
        }
    }, [campaignId, info])

    useEffect(() => {
        if (advertiserId) {
            loadAvailableCreatives()
            loadAvailableFilterLists()

            if (loaded.current) {
                form.setFieldValue('creativeIds', [])
                form.setFieldValue('filterListIds', [])
            } else {
                loaded.current = true
            }
        }
    }, [advertiserId])

    const loadCopyAdGroup = async () => {
        try {
            const response: any = await adGroupApi.view(copyAdGroupId)
            const bean = response.data

            bean.name = bean.name + ' copy'

            if (bean.startDate) {
                setStartDate(bean.startDate)
                bean.startDate = dayjs(bean.startDate, 'YYYY-MM-DD HH:mm')
            }
            if (bean.endDate) {
                setEndDate(bean.endDate)
                bean.endDate = dayjs(bean.endDate, 'YYYY-MM-DD HH:mm')
            }

            form.setFieldsValue(bean)

            setGeoItems(bean.geoItems)
            setSchedule(bean.schedule || ALWAYS_WORK)
            setMapPoint(fromBean(bean))
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        copyAdGroupId && loadCopyAdGroup()
    }, [copyAdGroupId])

    useEffect(() => {
        kwSearch && dictionariesApi
            .keywords(kwSearch)
            .then(setKwList)
    }, [debouncedKwSearch])

    const unlinkCreative = (record: CreativeBean) => {

        const list = form.getFieldValue('creativeIds')
        const index = list.indexOf(record.id)
        list.splice(index, 1)

        form.setFieldValue('creativeIds', [...list])
    }

    return <div className="anchored-form">
        <Form
            style={{
                width: '1000px'
            }}
            onFinish={() => handleSubmit(undefined)}
            form={form}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 16 }}
        >
            {error ? <Alert message={error} type="error" /> : null}
            <Card title="General" size="small" className="form-card" id="general">
                <FormItem
                    required
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: 'Ad Group name is required' }]}
                >
                    <Input placeholder="Name" />
                </FormItem>
                <FormItem
                    name="status"
                    label="Status"
                    required
                    rules={[{ required: true, message: 'Status is required' }]}
                >
                    <Select placeholder="Select Status">
                        {Object.values(AdGroupBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="campaignId"
                    required
                    label="Campaign"
                    rules={[{ required: true, message: 'Campaign ID is required' }]}
                >
                    <Select
                        showSearch
                        optionFilterProp="title"
                        placeholder="Select Campaign"
                    >
                        {info.campaigns.map((item: CampaignBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    required
                    name="domain"
                    label="Domain"
                    rules={[{ required: true, message: 'Advertiser domain is required' }]}
                >
                    <Input placeholder="Domain" />
                </FormItem>
                <FormItem
                    required
                    label="Dates"
                >
                    <FormItem
                        name="startDate"
                        className="ant-item ant-item-first form-date-item"
                        rules={[{ required: true, message: 'Ad Group start date is required' }]}
                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setStartDate(value as string)} placeholder="Start date" />
                    </FormItem>
                    <FormItem
                        name="endDate"
                        className="ant-item ant-item-second form-date-item"
                        rules={[{ required: true, message: 'Ad Group end date is required' }]}
                    >
                        <DatePicker showTime={{ defaultValue: dayjs('00:00', 'HH:mm') }} format="YYYY-MM-DD HH:mm" onChange={(moment, value) => setEndDate(value as string)} placeholder="End date" />
                    </FormItem>
                </FormItem>
            </Card>
            <Card title="Budget" size="small" className="form-card" id="budget">
                <FormItem
                    required
                    name="totalBudget"
                    label="Total Budget"
                    className="form-date-item"
                    rules={[{ required: true, message: 'Ad Group total budget is required' }]}
                >
                    <InputNumber placeholder="Total Budget" suffix="$" />
                </FormItem>
                <FormItem
                    name="dailyBudget"
                    label="Daily Budget"
                    className="form-date-item"
                >
                    <InputNumber placeholder="Daily Budget" suffix="$" />
                </FormItem>
                <FormItem
                    required
                    name="distribution"
                    label="Distribution"
                    rules={[{ required: true, message: 'Ad Group distribution is required' }]}
                >
                    <Select placeholder="Select Distribution Type">
                        {Object.values(AdGroupBean.DistributionEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    required
                    label="Pricing Type"
                >
                    <FormItem
                        name="biddingStrategy"
                        className="ant-item ant-item-first form-date-item"
                        rules={[{ required: true, message: 'Ad Group biddingStrategy is required' }]}
                    >
                        <Select allowClear placeholder="Select Pricing Type">
                            {Object.values(biddingStrategyEnum).map(type => <Option key={type} value={type}>{type == biddingStrategyEnum.CPMOptimized ? 'CPM Optimized' : type}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="price"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <InputNumber placeholder={getPriceLabel(biddingStrategy)} suffix="$" />
                    </FormItem>
                </FormItem>
                <FormItem
                    label="Frequency Capping"
                >
                    <FormItem
                        name="fcPeriod"
                        className="ant-item ant-item-first form-date-item"
                    >
                        <Select allowClear placeholder="Select FC period">
                            {Object.keys(AdGroupBean.FcPeriodEnum)
                                .filter(type => !antiFraudFcEnabled || type != FcPeriodEnum.Minute.toString())
                                .map(type => <Option key={type} value={type}>{type}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="fcLimit"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <InputNumber placeholder="FC limit" />
                    </FormItem>
                </FormItem>
            </Card>
            <Card title="Creatives" size="small" className="form-card" id="creatives">
                <FormItem
                    label="Link creative"
                >
                    <FormItem
                        name="creativeIds"
                        className={`ant-item ant-item-first form-date-item ag-creative-list-form ${creativeIds?.length > 0 ? 'ag-c-l-ph' : ''}`}
                    >
                        <Select
                            showSearch
                            allowClear
                            mode="multiple"
                            placeholder="Select Creative"
                            tagRender={(props) => <span />}
                        >
                            {availableCreatives.map((item: CreativeBean) => <Option key={item.id} value={item.id as number}>{item.name}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        className="ant-item ant-item-second"
                    >
                        <Button htmlType="button" type="primary" ghost style={{ width: 155, borderRadius: '8px', margin: '0 1% 0 0' }} icon={<PlusOutlined />} onClick={e => handleSubmit('/creative/create')}>
                            New Creative
                        </Button>
                    </FormItem>
                    <FormItem
                        className="ant-item ant-item-second"
                    >
                        <Button htmlType="button" type="primary" ghost style={{ width: 155, borderRadius: '8px' }} icon={<PlusOutlined />} onClick={e => handleSubmit('/creative/bulk')}>
                            New Bulk
                        </Button>
                    </FormItem>
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    <Table
                        rowKey="id"
                        size="small"
                        className="ag-creative-list-card"
                        dataSource={availableCreatives.filter((c: CreativeBean) => creativeIds?.includes(c.id))}
                        pagination={false}
                    >
                        <Column
                            key="id"
                            title="ID"
                            dataIndex="id"
                        />
                        <Column
                            key="name"
                            title="Name"
                            dataIndex="name"
                        />
                        <Column
                            key="format"
                            title="Format"
                            dataIndex="format"
                        />
                        <Column align="center" key="action" title="Actions" render={(text: string, record: CreativeBean) => (
                            <span>
                                <Button type="text" size="small" danger onClick={() => unlinkCreative(record)}>
                                    <DeleteOutlined />
                                </Button>
                            </span>
                        )} />
                    </Table>
                </FormItem>
            </Card>
            <Card title="Targeting" size="small" className="form-card" id="targeting">
                <FormItem
                    name="languages"
                    label="Languages"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select language"
                    >
                        {info.languages.map((item: LanguageBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="devices"
                    label="Devices"
                >
                    <Select mode="multiple" placeholder="Select device">
                        {Object.keys(AdGroupBean.DevicesEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="brands"
                    label="Brands"
                >
                    <Select mode="multiple" placeholder="Select brand">
                        {Object.keys(AdGroupBean.BrandsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="platforms"
                    label="Platforms"
                >
                    <Select mode="multiple" placeholder="Select platform">
                        {Object.keys(AdGroupBean.PlatformsEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="browsers"
                    label="Browsers"
                >
                    <Select mode="multiple" placeholder="Select browser">
                        {Object.keys(AdGroupBean.BrowsersEnum).map(item => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="contentIabCategoryIds"
                    label="Content IAB Categories"
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        placeholder="Select content IAB categories"
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    required
                    label="PMP Mode"
                >
                    <FormItem
                        name="pmpMode"
                        initialValue={pmpModeEnum.Public}
                        className="ant-item ant-item-first form-date-item"
                    >
                        <Select placeholder="Select Mode">
                            {Object.values(pmpModeEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="dealIdList"
                        className="ant-item ant-item-second form-date-item"
                    >
                        <Input disabled={pmpMode == pmpModeEnum.Public} placeholder="Deal ID list" />
                    </FormItem>
                </FormItem>
                <FormItem
                    name="filterListIds"
                    label="Filter Lists"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select Filter List"
                    >
                        {availableFilterLists.map((item: FilterListBean) => <Option title={item.name} key={item.id} value={item.id as number}>{item.name}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="retargetingListIds"
                    label="Retargeting Lists"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select Retargeting List"
                    >
                        {info.retargetingLists.map((item: RetargetingListBean) => <Option title={item.name} key={item.id} value={item.id as number}>{item.name}</Option>)}
                    </Select>
                </FormItem>
            </Card>
            <Card title="Contextual" size="small" className="form-card" id="contextual">
                <FormItem
                    name="keywords"
                    label="Keywords"
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        filterOption={false}
                        onSearch={setKwSearch}
                        placeholder="Search keywords"
                    >
                        {kwList.map((item: string) => <Option key={item} value={item}>{item}</Option>)}
                    </Select>
                </FormItem>
            </Card>
            <Card title="Day Parting" size="small" className="form-card" id="day-parting">
                <FormItem className="compact-ant-item" label="Monday">{renderDay(schedule[0])}</FormItem>
                <FormItem className="compact-ant-item" label="Tuesday">{renderDay(schedule[1])}</FormItem>
                <FormItem className="compact-ant-item" label="Wednesday">{renderDay(schedule[2])}</FormItem>
                <FormItem className="compact-ant-item" label="Thursday">{renderDay(schedule[3])}</FormItem>
                <FormItem className="compact-ant-item" label="Friday">{renderDay(schedule[4])}</FormItem>
                <FormItem className="compact-ant-item" label="Saturday">{renderDay(schedule[5])}</FormItem>
                <FormItem label="Sunday">{renderDay(schedule[6])}</FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 19 }}>
                    <Button htmlType="button" type="primary" ghost style={{ width: '23.8%', borderRadius: '8px' }} onClick={() => setScheduleVisible(true)} icon={<ScheduleOutlined />}>
                        Set Day Parting
                    </Button>
                </FormItem>
                <Scheduler
                    schedule={schedule}
                    visible={scheduleVisible}
                    setSchedule={setSchedule}
                    setVisible={setScheduleVisible}
                />
            </Card>
            <Card title="Geo" size="small" className="form-card" id="geo">
                <FormItem label="Coordinates" className="compact-ant-item">
                    Latitude: {mapPoint.lat ? mapPoint.lat : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }} className="compact-ant-item">
                    Longitude: {mapPoint.lon ? mapPoint.lon : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    Radius: {mapPoint.radius ? mapPoint.radius : <span className="form-item-text">not set</span>}
                </FormItem>
                <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                    <Button className="geo-btn" htmlType="button" type="primary" ghost style={{ width: '14.5rem', borderRadius: '8px' }} onClick={() => setMapVisible(true)} icon={<img src="/resources/images/map_point.svg" alt="" />}>
                        Set Geotargeting
                    </Button>
                </FormItem>
                <WithLoadedMap>
                    <MapSelector
                        point={mapPoint}
                        setPoint={setMapPoint}
                        mapVisible={mapVisible}
                        setMapVisible={setMapVisible}
                    />
                </WithLoadedMap>
                <FormItem
                    name="geoPrecision"
                    label="Geo Precision"
                >
                    <Select placeholder="Select Geo Precision">
                        {Object.values(precisionEnum).map((type: CampaignBean.GeoPrecisionEnum) => <Option key={type} value={type}>{getGeoPrecision(type)}</Option>)}
                    </Select>
                </FormItem>
                <GeoTargeting
                    id={copyAdGroupId || undefined}
                    loadGeo={adGroup => geoApi.adGroupGeo(adGroup)}
                    geoItems={geoItems}
                    setGeoItems={setGeoItems}
                />
            </Card>
            <Card title="Antifraud" size="small" className="form-card" id="general">
                <FormItem name="antiFraudFcEnabled" label="Frequency Capping" valuePropName="checked"
                    initialValue={false} tooltip="Disable minute-based frequency capping">
                    <Switch
                        size="small"
                        onChange={v => {
                            if (v && form.getFieldValue('fcPeriod') === FcPeriodEnum.Minute) {
                                form.setFieldValue('fcPeriod', undefined)
                                form.setFieldValue('fcLimit', undefined)
                            }
                        }}
                    />
                </FormItem>
            </Card>
            <div className="create-cancel-group">
                <Button type="primary" htmlType="submit">Create</Button>
                <Button htmlType="button" danger onClick={() => navigateTo(routes.AD_GROUP)}>Cancel</Button>
            </div>
        </Form>
        <AnchorWrapper items={anchorItems} />
    </div>
}

export default AdGroupCreate


// WEBPACK FOOTER //
// ./src/component/app/adGroup/AdGroupCreate.tsx